import { CreateListRequest, CreateTagRequest } from '@app/models/requests';
import { ListInterface } from '@app/models/util';
import { getCustomers } from '@app/services/customers';
import { createList, deleteManyLists, getLists, getPaginatedLists } from '@app/services/list';
import { createTag } from '@app/services/tag';
import {
  createListValidation,
  createTagValidation,
} from '@app/utils/validations';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';

export const useListPage = () => {
  const [addListOpen, setAddListOpen] = useState<boolean>(false);
  const [addTagOpen, setAddTagOpen] = useState<boolean>(false);
  const [addCustomersToList, setAddCustomersToList] = useState<boolean>(false);
  const [listQuery, setListQuery] = useState({ page: 1, limit: 10 });
  const [selectedLists, setSelectedLists] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState<string>('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const createListForm = useForm({ resolver: createListValidation });
  const createTagForm = useForm({ resolver: createTagValidation });
  const [openRemoveListModal, setOpenRemoveListModal] =
    useState<boolean>(false);
  const [amountOfSelectedCustomers, setAmountOfSelectedCustomers] =
    useState<number>(0);

  const { data: allListsData } = useQuery<{
    lists: ListInterface[];
    count: number;
  }>({
    queryKey: ['all-lists'],
    queryFn: () => {
      return getLists()
        .then((res) => res?.data)
        .catch((err) => err);
    },
  });

  const {
    data: listsData,
    isLoading,
    refetch,
  } = useQuery<{ lists: ListInterface[]; count: number }>({
    queryKey: ['lists-paginated', listQuery.page, listQuery.limit, sortBy, sortOrder],
    queryFn: () => {
      const limit = listQuery?.limit ?? 10;
      const page = listQuery?.page ?? 1;
      const skip = (page - 1) * limit;

      return getPaginatedLists({
        pagination: { limit, page, skip },
        sortBy,
        sortOrder,
      })
        .then((res) => res?.data)
        .catch((err) => err);
    },
  });

  const { data: customers } = useQuery({
    queryKey: ['customers-all'],
    queryFn: () => getCustomers(),
  });

  const handleAddList = () => {
    setAddListOpen(!addListOpen);
    if (!addListOpen) createListForm.reset();
  };

  const handleAddTag = () => {
    setAddTagOpen(!addTagOpen);
    if (!addTagOpen) createTagForm.reset();
  };

  const handleAddCustomersToList = () => {
    setAddCustomersToList(!addCustomersToList);
  };

  const handleDeleteListModal = () => {
    setOpenRemoveListModal(!openRemoveListModal);
  };

  const countCustomerPerList = (id: string | undefined) => {
    if (!id) return;
    const list = listsData?.lists.find((item) => item.id === id);
    const customerCount = list?.customers?.length ?? 0;
    const multiplier = selectedLists.includes(id) ? -1 : 1;
    setAmountOfSelectedCustomers(prev => prev + (customerCount * multiplier));
  };

  const handleSelectedLists = (id: string | undefined) => {
    countCustomerPerList(id);

    if (id) {
      if (id === 'all') {
        if (selectedLists.length === listsData?.lists.length) {
          setSelectedLists([]);
          setAmountOfSelectedCustomers(0);
        } else {
          setSelectedLists(listsData?.lists.map((item) => item.id) ?? []);
          setAmountOfSelectedCustomers((listsData?.lists ?? []).reduce((acc, item) => {
            return acc + (item.customers?.length ?? 0);
          }, 0));
        }
        return;
      }

      if (selectedLists.includes(id)) {
        setSelectedLists((prev) => prev.filter((item) => item !== id));
      } else {
        setSelectedLists((prev) => [...prev, id]);
      }
    }
  };

  const { mutateAsync: addList, isPending: isPendingList } = useMutation({
    mutationKey: ['add-list'],
    mutationFn: (data: CreateListRequest) => createList(data),
    onSuccess: () => {
      refetch();
      handleAddList();
      createListForm.reset();
    },
    onError: (err) => {
      toast.error('Error creating list');
      console.error(err);
    },
  });
  const { mutateAsync: addTag, isPending: isPendingTag } = useMutation({
    mutationKey: ['add-tag'],
    mutationFn: (data: CreateTagRequest) => createTag(data),
    onSuccess: () => {
      handleAddTag();
      createTagForm.reset();
    },
    onError: (err) => {
      toast.error('Error creating tag');
      console.error(err);
    },
  });

  const submitList = async (values: FieldValues) => {
    await addList(values as CreateListRequest);
  };

  const submitTag = async (values: FieldValues) => {
    await addTag(values as CreateTagRequest);
  };

  const handleNextPage = () => {
    setListQuery((prev) => ({ ...prev, page: prev.page + 1 }));
  };

  const handlePrevPage = () => {
    setListQuery((prev) => ({
      ...prev,
      page: prev.page === 0 ? 0 : prev.page - 1,
    }));
  };

  const handleLimit = (value: any) => {
    setListQuery((prev) => ({ ...prev, limit: value }));
  };

  const handleSortingTable = (field: string) => {
    if (sortBy === field) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const deleteSelectedLists = async () => {
    await deleteManyLists(selectedLists.map(Number));
    setSelectedLists([]);
    setAmountOfSelectedCustomers(0);
    setListQuery((prev) => ({ ...prev, page: 1 }));
    refetch();
    handleDeleteListModal();
  };

  useEffect(() => {
    if (listsData) {
      refetch();
    }
  }, [listQuery.limit, listQuery.page, sortBy, sortOrder]);

  return {
    addListOpen,
    addTagOpen,
    handleAddList,
    handleAddTag,
    createListForm,
    createTagForm,
    submitList,
    submitTag,
    isPendingList,
    isPendingTag,
    listsData,
    isLoading,
    customers,
    handleNextPage,
    handlePrevPage,
    handleLimit,
    listQuery,
    refetch,
    handleAddCustomersToList,
    addCustomersToList,
    handleSelectedLists,
    selectedLists,
    allListsData,
    openRemoveListModal,
    handleDeleteListModal,
    amountOfSelectedCustomers,
    deleteSelectedLists,
    sortBy,
    sortOrder,
    handleSortingTable,
  };
};
